import React, { useState, useEffect, useCallback, useRef } from 'react';

const URGENCY_STYLES = {
  info: {
    background: 'rgba(99, 102, 241, 0.08)',
    border: '#6366f1',
    label: 'Suggestion',
  },
  warning: {
    background: 'rgba(251, 191, 36, 0.08)',
    border: '#fbbf24',
    label: 'Heads Up',
  },
  critical: {
    background: 'rgba(248, 113, 113, 0.08)',
    border: '#f87171',
    label: 'Critical',
  },
  positive: {
    background: 'rgba(34, 197, 94, 0.08)',
    border: '#22c55e',
    label: 'Nice Work',
  },
};

const URGENCY_TIMEOUT = { critical: 0, warning: 30000, info: 20000, positive: 15000 };

const FEEDBACK = {
  accepted: { text: '\u2713 Accepted', bg: '#059669', color: '#fff' },
  dismissed: { text: '\u2717 Dismissed', bg: 'rgba(255,255,255,0.1)', color: '#94a3b8' },
};

function playCriticalChime() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.setValueAtTime(1100, ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.25);
  } catch { /* audio not available */ }
}

function timeAgo(ts) {
  if (!ts) return '';
  const sec = Math.round((Date.now() - ts) / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  return `${min}m ago`;
}

function HistoryRail({ items }) {
  const valid = items?.filter(Boolean);
  if (!valid || valid.length === 0) return null;
  return (
    <div className="suggestion-history-rail">
      {valid.map((item, i) => {
        const style = URGENCY_STYLES[item.urgency] || URGENCY_STYLES.info;
        return (
          <div key={item.id || i} className="suggestion-history-pill">
            <span className="suggestion-history-dot" style={{ background: style.border }} />
            <span className="suggestion-history-text">{item.text}</span>
            {item.arrivedAt && (
              <span className="suggestion-history-time">{timeAgo(item.arrivedAt)}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

function PinnedCard({ item }) {
  if (!item) return null;
  const urgency = item.urgency || 'info';
  const style = URGENCY_STYLES[urgency] || URGENCY_STYLES.info;
  const label = urgency === 'positive' ? 'Last \u2014 Nice Work' : 'Last Tip';
  return (
    <div
      className="whisper-panel whisper-pinned"
      style={{
        background: style.background,
        border: `1px dashed ${style.border}40`,
        borderLeftWidth: '4px',
        borderLeftStyle: 'dashed',
        borderLeftColor: `${style.border}60`,
        padding: '16px 20px',
        borderRadius: '14px',
        marginBottom: '18px',
        opacity: 0.45,
        transition: 'opacity 0.3s',
      }}
      role="status"
      aria-live="polite"
    >
      <div style={{
        fontSize: '11px', fontWeight: 700, color: style.border,
        textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '6px',
        opacity: 0.7,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: '17px', fontWeight: 600,
        color: 'var(--mppc-text-heading)', lineHeight: 1.4,
      }}>
        {item.text}
      </div>
    </div>
  );
}

export default function WhisperPanel({ suggestion, lastSuggestion, suggestionHistory, streamingTokens, onAction }) {
  const [feedback, setFeedback] = useState(null);
  const feedbackTimer = useRef(null);
  const lastSuggestionId = useRef(null);
  const [countdownPct, setCountdownPct] = useState(100);
  const countdownRef = useRef(null);

  useEffect(() => {
    if (suggestion?.id && suggestion.id !== lastSuggestionId.current) {
      lastSuggestionId.current = suggestion.id;
      setFeedback(null);

      if (suggestion.urgency === 'critical') {
        playCriticalChime();
      }

      const timeout = URGENCY_TIMEOUT[suggestion.urgency] ?? URGENCY_TIMEOUT.info;
      if (countdownRef.current) cancelAnimationFrame(countdownRef.current);

      if (timeout > 0) {
        const start = Date.now();
        const tick = () => {
          const elapsed = Date.now() - start;
          const remaining = Math.max(0, 1 - elapsed / timeout);
          setCountdownPct(remaining * 100);
          if (remaining > 0) countdownRef.current = requestAnimationFrame(tick);
        };
        countdownRef.current = requestAnimationFrame(tick);
      } else {
        setCountdownPct(100);
      }
    }
  }, [suggestion?.id, suggestion?.urgency]);

  useEffect(() => {
    return () => { if (countdownRef.current) cancelAnimationFrame(countdownRef.current); };
  }, []);

  const handleAction = useCallback((id, action) => {
    setFeedback(action);
    if (countdownRef.current) cancelAnimationFrame(countdownRef.current);
    if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
    feedbackTimer.current = setTimeout(() => {
      onAction(id, action);
      setFeedback(null);
    }, 600);
  }, [onAction]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!suggestion || feedback || suggestion.urgency === 'positive') return;
      if (e.key === 'Escape') {
        handleAction(suggestion.id, 'dismissed');
      } else if (e.key === 'Enter') {
        handleAction(suggestion.id, 'accepted');
      }
    },
    [suggestion, feedback, handleAction]
  );

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  useEffect(() => {
    return () => { if (feedbackTimer.current) clearTimeout(feedbackTimer.current); };
  }, []);

  const showCard = suggestion || streamingTokens || feedback;

  return (
    <>
      {showCard && feedback && (
        <div
          className="whisper-panel"
          style={{
            background: FEEDBACK[feedback].bg,
            padding: '20px 24px',
            borderRadius: '12px',
            marginBottom: '16px',
            textAlign: 'center',
            animation: 'slideIn 0.15s ease-out',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ fontSize: '20px', fontWeight: 700, color: FEEDBACK[feedback].color }}>
            {FEEDBACK[feedback].text}
          </div>
        </div>
      )}

      {showCard && !feedback && (() => {
        const urgency = suggestion?.urgency || 'info';
        const style = URGENCY_STYLES[urgency] || URGENCY_STYLES.info;
        const displayText = suggestion?.text || streamingTokens;
        const isCritical = urgency === 'critical';
        const timeout = URGENCY_TIMEOUT[urgency] ?? URGENCY_TIMEOUT.info;

        const len = (displayText || '').length;
        const isLong = len > 200;
        const fontSize = len > 200 ? '16px' : len > 80 ? '19px' : '24px';

        const isPositive = urgency === 'positive';
        const actionButtons = suggestion && !isPositive && (
          <div style={{
            display: 'flex', gap: '8px', flexShrink: 0,
            ...(isLong ? { marginTop: '12px' } : { marginLeft: '16px' }),
          }}>
            <button
              onClick={() => handleAction(suggestion.id, 'accepted')}
              style={{
                background: 'linear-gradient(135deg, #22c55e, #16a34a)',
                color: 'white', border: 'none', borderRadius: '8px',
                padding: isLong ? '8px 20px' : '10px 18px',
                cursor: 'pointer', fontSize: '14px',
                fontWeight: 700, display: 'flex',
                flexDirection: isLong ? 'row' : 'column',
                alignItems: 'center', gap: isLong ? '6px' : '2px',
                transition: 'transform 0.1s, box-shadow 0.1s',
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.3)',
              }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.95)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <span style={{ fontSize: '16px', lineHeight: 1 }}>{'\u2713'}</span>
              <span>Accept</span>
              <span style={{ fontSize: '10px', fontWeight: 400, opacity: 0.8 }}>Enter</span>
            </button>
            <button
              onClick={() => handleAction(suggestion.id, 'dismissed')}
              style={{
                background: 'rgba(255, 255, 255, 0.08)', color: '#94a3b8',
                border: '1px solid rgba(255, 255, 255, 0.1)', borderRadius: '8px',
                padding: isLong ? '8px 20px' : '10px 18px',
                cursor: 'pointer', fontSize: '14px',
                fontWeight: 700, display: 'flex',
                flexDirection: isLong ? 'row' : 'column',
                alignItems: 'center', gap: isLong ? '6px' : '2px',
                transition: 'transform 0.1s, box-shadow 0.1s',
              }}
              onMouseDown={(e) => { e.currentTarget.style.transform = 'scale(0.95)'; }}
              onMouseUp={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
            >
              <span style={{ fontSize: '16px', lineHeight: 1 }}>{'\u2717'}</span>
              <span>Dismiss</span>
              <span style={{ fontSize: '10px', fontWeight: 400, opacity: 0.8 }}>Esc</span>
            </button>
          </div>
        );

        return (
          <div
            className={`whisper-panel ${isCritical ? 'whisper-critical' : ''}`}
            style={{
              background: style.background,
              border: `1px solid ${style.border}30`,
              borderLeftWidth: '4px',
              borderLeftColor: style.border,
              padding: '20px 24px 16px',
              borderRadius: '14px',
              marginBottom: '18px',
              animation: 'slideIn 0.2s ease-out',
              backdropFilter: 'blur(10px)',
              position: 'relative',
            }}
            role="alert"
            aria-live="assertive"
          >
            {isLong ? (
              <>
                <div
                  style={{
                    fontSize: '13px', fontWeight: 700, color: style.border,
                    textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px',
                  }}
                >
                  {style.label}
                </div>
                <div
                  style={{
                    fontSize, fontWeight: 600,
                    color: 'var(--mppc-text-heading)', lineHeight: 1.45,
                    maxHeight: '200px', overflowY: 'auto',
                  }}
                >
                  {displayText}
                  {!suggestion && streamingTokens && (
                    <span className="cursor-blink">|</span>
                  )}
                </div>
                {suggestion?.reason && (
                  <div style={{ fontSize: '13px', color: 'var(--mppc-text-body)', marginTop: '8px', lineHeight: 1.4 }}>
                    {suggestion.reason}
                  </div>
                )}
                {actionButtons}
              </>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: '13px', fontWeight: 700, color: style.border,
                        textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px',
                      }}
                    >
                      {style.label}
                    </div>
                    <div
                      style={{
                        fontSize, fontWeight: 600,
                        color: 'var(--mppc-text-heading)', lineHeight: 1.4,
                      }}
                    >
                      {displayText}
                      {!suggestion && streamingTokens && (
                        <span className="cursor-blink">|</span>
                      )}
                    </div>
                  </div>
                  {actionButtons}
                </div>
                {suggestion?.reason && (
                  <div style={{ fontSize: '14px', color: 'var(--mppc-text-body)', marginTop: '10px', lineHeight: 1.4 }}>
                    {suggestion.reason}
                  </div>
                )}
              </>
            )}
            {timeout > 0 && suggestion && (
              <div style={{
                position: 'absolute', bottom: 0, left: 0, right: 0,
                height: '3px', overflow: 'hidden',
                borderRadius: '0 0 14px 14px',
              }}>
                <div className="suggestion-countdown" style={{ width: `${countdownPct}%` }} />
              </div>
            )}
          </div>
        );
      })()}

      {!showCard && lastSuggestion && <PinnedCard item={lastSuggestion} />}

      <HistoryRail items={suggestionHistory} />
    </>
  );
}
